/**
 * Saved chart datasets — vault record shape and pure operations for the
 * chart tools' "My datasets" feature (PlotShell). One record per
 * (user, chart tool id), stored locally in IndexedDB via the vault.
 */

export interface SavedChartDataset {
  id: string;
  name: string;
  text: string;
  savedAt: number;
}

export interface ChartDatasetVault {
  version: 1;
  datasets: SavedChartDataset[];
  /** Most recent textarea content, auto-restored on the next visit. */
  lastText?: string;
}

/** Oldest saved datasets are dropped beyond this per-tool cap. */
export const MAX_DATASETS = 20;

export function emptyChartVault(): ChartDatasetVault {
  return { version: 1, datasets: [] };
}

/** Add a named dataset (newest first), dropping the oldest beyond the cap. */
export function addDataset(
  vault: ChartDatasetVault | null,
  name: string,
  text: string,
  now: number = Date.now(),
): ChartDatasetVault {
  const base = vault ?? emptyChartVault();
  const dataset: SavedChartDataset = {
    id: `ds_${now}_${Math.random().toString(36).slice(2, 8)}`,
    name: name.trim() || defaultDatasetName(base),
    text,
    savedAt: now,
  };
  return {
    ...base,
    datasets: [dataset, ...base.datasets].slice(0, MAX_DATASETS),
  };
}

export function removeDataset(
  vault: ChartDatasetVault,
  id: string,
): ChartDatasetVault {
  return { ...vault, datasets: vault.datasets.filter((d) => d.id !== id) };
}

export function withLastText(
  vault: ChartDatasetVault | null,
  lastText: string,
): ChartDatasetVault {
  return { ...(vault ?? emptyChartVault()), lastText };
}

/** "Dataset N" fallback when the user saves without typing a name. */
export function defaultDatasetName(vault: ChartDatasetVault | null): string {
  return `Dataset ${(vault?.datasets.length ?? 0) + 1}`;
}
